import * as classNames from 'classnames';
import * as React from 'react';
import { Col, Row } from 'reactstrap';
import { compose, mapProps } from 'recompose';
import {
    ActivityChartContainerProps,
    ActivityChartProps,
    AthleteProps,
    SaveAthleteOptionsProps,
    ScalarStream,
    StravaStreamType
} from '../../types';
import './ActivityChart.scss';
import ActivityChartControls, {
    ActivityChartControlsProps
} from './ActivityChartControls';
import LazyActivityChart from './LazyActivityChart';

interface ActivityChartRowProps {
    style?: React.CSSProperties;
    chartProps: ActivityChartProps;
    controlsProps: ActivityChartControlsProps;
}

/** Extract the props for the child components to make things a bit tidier */
const convertProps = ({
    activity,
    error,
    isSelected,
    loading,
    ...props
}: ActivityChartContainerProps &
    SaveAthleteOptionsProps &
    AthleteProps): ActivityChartRowProps => {
    const streams =
        activity && activity.scalarStreams ? activity.scalarStreams : [];

    const xAxisStreams = props.xAxisStreamTypes.reduce(
        (
            acc: ScalarStream[],
            t: StravaStreamType.distance | StravaStreamType.time
        ) => {
            const stream = streams.find(s => s.type === t);
            stream && acc.push(stream);
            return acc;
        },
        []
    );

    if (!props.yAxisStreamType) {
        throw new Error(
            'No Y axis stream type specified for activity chart row'
        );
    }

    const yAxisStream = streams.find(s => s.type === props.yAxisStreamType);

    const sharedProps = {
        error,
        isSelected,
        loading
    };

    const controlsProps: ActivityChartControlsProps = {
        ...sharedProps,
        isBottomItem: props.isBottomItem,
        isEmpty: !loading && !!(yAxisStream && yAxisStream.isEmpty),
        isTopItem: props.isTopItem,
        moveActivityStream: props.moveActivityStream,
        name: props.name,
        removeActivityStream: props.removeActivityStream,
        selectActivityStream: props.selectActivityStream,
        type: props.yAxisStreamType
    };

    const chartProps: ActivityChartProps = {
        ...sharedProps,
        autoplayAnimation:
            (props.loggedInUser &&
                props.loggedInUser.user &&
                props.loggedInUser.user.athlete &&
                props.loggedInUser.user.athlete.autoplayAnimation) ||
            false,
        color: props.color,
        height: props.height,
        id: activity ? activity.id : undefined,
        unitsPreference: props.unitsPreference,
        xAxisStreams,
        yAxisStream
    };

    return {
        chartProps,
        controlsProps,
        style: props.style
    };
};

const ActivityChartRow = ({
    style,
    controlsProps,
    chartProps
}: ActivityChartRowProps) => {
    const { yAxisStream, isSelected } = chartProps;

    let errorMessage: string | undefined;

    if (controlsProps.error || chartProps.error) {
        errorMessage = 'Error loading chart';
    } else if (yAxisStream && yAxisStream.isEmpty) {
        errorMessage = `No ${yAxisStream.friendlyName.toLowerCase()} data available for this activity`;
    }

    return (
        <div
            style={style}
            className={classNames('activity-chart-container', {
                active: isSelected
            })}
            // tslint:disable-next-line:jsx-no-lambda
            onClick={(e: React.MouseEvent<any>) => {
                const { selectActivityStream, type } = controlsProps;
                selectActivityStream && selectActivityStream(type || undefined);
            }}
        >
            <Row>
                <Col md="3">
                    <ActivityChartControls {...controlsProps} />
                </Col>
                <Col md="9" className="pt-2">
                    <div>
                        {errorMessage ? (
                            <h6 className="text-center mt-5 text-muted">
                                {errorMessage}
                            </h6>
                        ) : (
                            <LazyActivityChart {...chartProps} />
                        )}
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default compose<
    ActivityChartRowProps,
    ActivityChartContainerProps & SaveAthleteOptionsProps & AthleteProps
>(mapProps(convertProps))(ActivityChartRow);



// WEBPACK FOOTER //
// ./src/client/components/Strava/ActivityScene/ActivityChart/ActivityChartRow.tsx